import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { OptionType } from '@fullstory/components';
import {
  pagesSelectors,
  getTopUnmappedSpecObjects,
  useFetchFsSpec,
  TargetType,
  allSpecPagesBySlug,
} from '@fullstory/redux';
import {
  ExploreMenu,
  getSelectedItemLabel,
  Metadata,
  ResourceSelectMenu,
  UNMAPPED_SPEC_THRESHOLD,
} from '@fullstory/library-ui';
import { PageDefinitionBody } from '../PageDefinition';
import { getPageMetadata, getPageName } from '../../utils/utils';
import { usePageSearch } from '../../hooks/usePageSearch';
import { isNonNull } from '@fullstory/typeutils';
import { isEqual, remove } from 'lodash';
import { useFlags } from '@fullstory/react-hooks';
import { PageRefValue, PageRefValueType } from '@fullstory/search-types';
import { SpecPageBody } from '../SpecPageBody';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { intl } from '@fullstory/intl';
import { toDxObjectsMetadata } from '@fullstory/util/auditmetadata';
import { usePageModalProps, PageDefModalComponent } from '../../hooks/usePageDefModal';
import { EMPTY_PAGE_DEF } from '../../constants';

const allPagesGroup = 'All Pages';

const { pagesByIdWithPageDefZero } = pagesSelectors;

interface PageSelectMenuProps
  extends Pick<
    React.ComponentProps<typeof ResourceSelectMenu>,
    'variant' | 'fullWidth' | 'compact' | 'size' | 'placeholder' | 'initialOpen' | 'onClose'
  > {
  pageId: PageRefValue | null;
  setPageId: (pageDefId: PageRefValue | null) => void;

  /** Don't add a 'Not defined' option as the first option with a value of 0. */
  excludeNotDefined?: boolean;

  /** Render the optional header call to action */
  includeCallToAction?: boolean;

  /** Don't render spec pages.  Need to prevent circular dependencies when mapping spec pages */
  omitSpecPages?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  onCreate?: React.ComponentProps<typeof ResourceSelectMenu>['onCreate'];
}

export const PageSelectMenu = ({
  pageId,
  setPageId,
  variant,
  fullWidth,
  compact,
  size,
  placeholder,
  initialOpen,
  onClose,
  excludeNotDefined,
  includeCallToAction,
  omitSpecPages,
  disabled,
  clearable,
  onCreate,
  ...restOfProps
}: PageSelectMenuProps) => {
  const flags = useFlags();

  const [pendingSpecSlug, setPendingSpecSlug] = useState<string>();
  const pageModalProps = usePageModalProps();
  const hasFSTA = flags.hasBit(Flag.FeatFSTA);

  const onPageDefModalClose = useCallback(
    (saved?: boolean) => {
      if (pendingSpecSlug) {
        if (saved) {
          // if saved, then we can finally commit the spec page
          setPageId({ type: PageRefValueType.Spec, value: pendingSpecSlug });
        }
        // and no matter what, clear the pending state
        setPendingSpecSlug(undefined);
      }
    },
    [pendingSpecSlug, setPendingSpecSlug, setPageId],
  );

  const pageDefinitionsById = useSelector(pagesByIdWithPageDefZero);

  useFetchFsSpec();
  const specPagesBySlug = useSelector(allSpecPagesBySlug);
  const topUnmappedSpecObjects = useSelector(getTopUnmappedSpecObjects);
  const canModifySpec = flags.has(Flag.PermModifySpecMapping);

  const topUnmappedSpecSlugs = useMemo(
    () =>
      topUnmappedSpecObjects
        .filter(object => object.mappableTypes.includes(TargetType.Page))
        .map(object => object.slug),
    [topUnmappedSpecObjects],
  );

  const options = useMemo(() => {
    const activeOptions = [...pageDefinitionsById.values()].filter(
      p => (pageId && pageId.type === PageRefValueType.Id && p.id === pageId.value) || !p.archived,
    );
    const activeOptionsCount = activeOptions.length - 1; // - 1 for 'not defined' option
    const idOptions: OptionType<PageRefValue>[] = activeOptions.map(p => ({
      group: allPagesGroup,
      key: p.id.toString(),
      value: {
        type: PageRefValueType.Id,
        value: p.id,
      },
      label: getSelectedItemLabel({ name: getPageName(p), archived: p.archived }),
    }));
    idOptions.sort((opt1, opt2) => opt1.label.localeCompare(opt2.label));
    if (excludeNotDefined) {
      // Manually remove 'Not defined' option
      remove(idOptions, o => o.value.value === 0);
    }

    const mappedSlugs = omitSpecPages
      ? []
      : Object.values(specPagesBySlug)
          .filter(object => object.active && object.mapping)
          .map(object => object.slug);

    const showUnmappedSpec =
      activeOptionsCount < UNMAPPED_SPEC_THRESHOLD && canModifySpec && !omitSpecPages;
    const unmappedSpecSlugs = showUnmappedSpec ? topUnmappedSpecSlugs : [];

    const makeSpecOption = (
      objectSlug: string,
      unmapped?: boolean,
    ): OptionType<PageRefValue> | undefined => {
      if (!objectSlug) {
        return undefined;
      }

      const specObject = specPagesBySlug[objectSlug];
      if (!specObject) {
        return undefined;
      }

      const group = unmapped
        ? intl.formatMessage(
            {
              defaultMessage: 'Recommended to create: {specName}',
              id: 'U3mhdc',
            },
            { specName: specObject.spec.displayName },
          )
        : specObject.spec.displayName;

      return {
        group,
        key: objectSlug,
        value: {
          type: PageRefValueType.Spec,
          value: objectSlug,
        },
        label: getSelectedItemLabel({
          name: specObject.displayName,
          archived: false,
        }),
      };
    };

    const specOptions: OptionType<PageRefValue>[] = mappedSlugs
      .map(slug => makeSpecOption(slug))
      .filter(isNonNull);
    // Sort spec options by group, and by label within groups.
    specOptions.sort((opt1, opt2) => {
      const groupOrdering = opt1.group!.localeCompare(opt2.group!);
      if (groupOrdering !== 0) return groupOrdering;
      return opt1.label.localeCompare(opt2.label);
    });
    // Unampped spec options are already ordered by priority. We don't want to sort differently.
    const unmappedSpecOptions: OptionType<PageRefValue>[] = unmappedSpecSlugs
      .map(slug => makeSpecOption(slug, true))
      .filter(isNonNull);

    return [...specOptions, ...idOptions, ...unmappedSpecOptions];
  }, [
    excludeNotDefined,
    pageDefinitionsById,
    pageId,
    specPagesBySlug,
    omitSpecPages,
    canModifySpec,
    topUnmappedSpecSlugs,
  ]);

  const selected = useMemo(
    () => options.find(o => isEqual(o.value, pageId)) ?? null,
    [options, pageId],
  );

  const idPages = useMemo(
    () =>
      options
        .map(item => {
          if (item.value.type !== PageRefValueType.Id) {
            return undefined;
          }
          return pageDefinitionsById.get(item.value.value);
        })
        .filter(isNonNull),
    [options, pageDefinitionsById],
  );

  const onSearch = usePageSearch();

  const filterFn = useCallback(
    (items: OptionType<PageRefValue>[], filterText: string) =>
      onSearch(idPages, filterText).then(searchResults =>
        items.filter(item => {
          switch (item.value.type) {
            case PageRefValueType.Id:
              return searchResults.find(page => page.id === item.value.value);
            case PageRefValueType.Spec: {
              // If the spec name matches, we're good
              if (item.label.toLowerCase().includes(filterText.toLowerCase())) {
                return true;
              }

              // If the spec name doesn't match, dereference the spec, and see if any of the underlying mappings match
              const pageId = specPagesBySlug[item.value.value]?.mapping?.pageId;
              if (!pageId) {
                return false;
              }

              return searchResults.find(page => page.id === pageId);
            }
          }
        }),
      ),
    [onSearch, idPages, specPagesBySlug],
  );

  const onChange = useCallback(
    (option: OptionType<PageRefValue> | null) => {
      // if an unmapped spec object, we need to open the create modal
      // we don't want to set the page yet
      if (
        option?.value?.type === PageRefValueType.Spec &&
        topUnmappedSpecSlugs.includes(option.value.value)
      ) {
        setPendingSpecSlug(option.value.value);
        const newPage = { ...EMPTY_PAGE_DEF };
        // default the new page name to the name of the spec page
        newPage.name = specPagesBySlug[option.value.value]?.displayName || '';
        pageModalProps.setModalInput(newPage);
        return;
      }
      // otherwise, we do handle clearing / setting
      if (clearable) {
        setPageId(option?.value || null);
      } else if (option) {
        setPageId(option.value);
      }
    },
    [
      topUnmappedSpecSlugs,
      setPageId,
      clearable,
      setPendingSpecSlug,
      pageModalProps.setModalInput,
      specPagesBySlug,
    ],
  );

  const renderDefinition = useCallback(
    (option: OptionType<PageRefValue>) => {
      switch (option.value.type) {
        case PageRefValueType.Id: {
          const pageDefinition = option.value ? pageDefinitionsById.get(option.value.value) : null;
          return pageDefinition && <PageDefinitionBody pageDefinition={pageDefinition} />;
        }
        case PageRefValueType.Spec: {
          if (topUnmappedSpecSlugs.includes(option.value.value)) {
            return undefined;
          }
          return <SpecPageBody slug={option.value.value} />;
        }
      }
    },
    [pageDefinitionsById, topUnmappedSpecSlugs],
  );

  const getMetadata = useCallback(
    (option: OptionType<PageRefValue>): Metadata | null => {
      switch (option.value.type) {
        case PageRefValueType.Id: {
          const pageDefinition = option.value && pageDefinitionsById.get(option.value.value);
          return pageDefinition ? getPageMetadata(pageDefinition) : null;
        }
        case PageRefValueType.Spec: {
          const mapping = specPagesBySlug[option.value.value]?.mapping;
          if (!mapping) {
            return null;
          }
          return toDxObjectsMetadata(mapping.metadata);
        }
      }
    },
    [pageDefinitionsById, specPagesBySlug],
  );

  const renderAction = useCallback(
    (option: OptionType<PageRefValue>) => {
      switch (option.value.type) {
        case PageRefValueType.Id: {
          const pageDefinition = option.value ? pageDefinitionsById.get(option.value.value) : null;
          return pageDefinition && <ExploreMenu pageDefinition={pageDefinition} />;
        }
        case PageRefValueType.Spec:
          return null;
      }
    },
    [pageDefinitionsById],
  );

  return (
    <>
      <ResourceSelectMenu<PageRefValue>
        options={options}
        selected={selected}
        onChange={onChange}
        variant={variant}
        fullWidth={fullWidth}
        compact={compact}
        size={size}
        placeholder={placeholder}
        itemType="Page"
        clearable={clearable}
        onCreate={onCreate}
        renderDefinition={renderDefinition}
        filterFn={filterFn}
        initialOpen={initialOpen}
        onClose={onClose}
        disabled={disabled}
        getMetadata={getMetadata}
        renderAction={
          includeCallToAction && flags.featureFlags().AppGlidePaths && hasFSTA
            ? renderAction
            : undefined
        }
        {...restOfProps}
      />
      <PageDefModalComponent
        initialSpecSlugs={pendingSpecSlug ? [pendingSpecSlug] : undefined}
        {...pageModalProps}
        onClose={onPageDefModalClose}
      />
    </>
  );
};
